import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AdminUser } from '@/types/database'

interface AuditLogWithUser {
  id: string
  admin_id: string
  action: string
  details: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  admin_users: {
    email: string
    role: string
  } | null
}

interface UserActivitySummary {
  id: string
  email: string
  role: string
  sign_in_count: number
  last_sign_in: string | null
  last_action: string | null
  last_action_at: string | null
  total_actions: number
}

export function useAuditLogs({ actionFilter, limit = 50 }: { actionFilter?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ['audit-logs', actionFilter, limit],
    queryFn: async (): Promise<AuditLogWithUser[]> => {
      let query = supabase
        .from('admin_audit_logs')
        .select('*, admin_users(email, role)')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (actionFilter) {
        if (actionFilter === 'sign_ins') {
          query = query.eq('action', 'login_success')
        } else if (actionFilter === 'sign_outs') {
          query = query.eq('action', 'logout')
        } else if (actionFilter === 'mfa_setup') {
          query = query.in('action', ['mfa_setup_complete', 'mfa_disabled'])
        } else if (actionFilter === 'role_changes') {
          query = query.eq('action', 'user_role_updated')
        } else if (actionFilter === 'user_deletions') {
          query = query.eq('action', 'user_deleted')
        }
      }

      const { data, error } = await query

      if (error) throw error
      return data as AuditLogWithUser[]
    },
  })
}

export function useUserActivitySummary() {
  return useQuery({
    queryKey: ['user-activity-summary'],
    queryFn: async (): Promise<UserActivitySummary[]> => {
      const [usersResult, logsResult] = await Promise.all([
        supabase.from('admin_users').select('*').order('created_at', { ascending: true }),
        supabase.from('admin_audit_logs').select('*').order('created_at', { ascending: false }),
      ])

      if (usersResult.error) throw usersResult.error
      if (logsResult.error) throw logsResult.error

      const users = usersResult.data as AdminUser[]
      const logs = logsResult.data as { admin_id: string; action: string; created_at: string }[]

      return users.map((user) => {
        const userLogs = logs.filter((log) => log.admin_id === user.id)
        const loginLogs = userLogs.filter((log) => log.action === 'login_success')
        const latestLog = userLogs[0] || null

        return {
          id: user.id,
          email: user.email,
          role: user.role,
          sign_in_count: loginLogs.length,
          last_sign_in: user.last_login,
          last_action: latestLog?.action || null,
          last_action_at: latestLog?.created_at || null,
          total_actions: userLogs.length,
        }
      })
    },
  })
}
